import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

/** Fallback re-read while the status socket is down or quiet. */
export const BOOTSTRAP_POLL_INTERVAL_MS = 30000;
/** Burst of queue_update frames (one per write) collapses into one read. */
export const BOOTSTRAP_REFRESH_DEBOUNCE_MS = 250;

const EMPTY_HIGHLIGHTS = { mostRecentTip: null, topDonor: null };

/**
 * The listener stream page's start state (ENG-667, backed by
 * `GET /public/bands/{id}/stream/bootstrap`, ENG-668): band header, crate
 * preview, now-playing card, setlist rail and chat highlights.
 *
 * `refresh()` re-reads it; the page calls it on every `queue_update` and live
 * flip from the status socket. `setHighlights()` applies a
 * `stream_highlights_update` frame without a round trip.
 */
export const useStreamBootstrap = (bandId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetchWrapperRef = useRef(useFetchWrapper());
  const debounceRef = useRef(null);
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    if (!bandId) return;
    const seq = ++seqRef.current;
    try {
      const payload = await fetchWrapperRef.current.get(
        `${config.apiBaseUrl}/public/bands/${bandId}/stream/bootstrap`
      );
      // A slower, older read must not overwrite a newer one.
      if (seq !== seqRef.current) return;
      setData(normalizeBootstrap(payload));
      setError(false);
    } catch {
      if (seq === seqRef.current) setError(true);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [bandId]);

  const refresh = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, BOOTSTRAP_REFRESH_DEBOUNCE_MS);
  }, [load]);

  const setHighlights = useCallback((highlights) => {
    setData((prev) => (prev ? { ...prev, highlights: normalizeHighlights(highlights) } : prev));
  }, []);

  useEffect(() => {
    setLoading(true);
    setData(null);
    load();
    const interval = setInterval(load, BOOTSTRAP_POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(debounceRef.current);
      seqRef.current += 1;
    };
  }, [load]);

  return { data, loading, error, refresh, setHighlights };
};

const normalizeHighlights = (h) => ({
  mostRecentTip: h?.mostRecentTip ?? null,
  topDonor: h?.topDonor ?? null,
});

/** Defaults for every field the page reads, so a partial payload cannot crash it. */
export const normalizeBootstrap = (payload) => ({
  band: {
    id: payload?.band?.id ?? null,
    name: payload?.band?.name ?? '',
    avatarUrl: payload?.band?.avatarUrl ?? null,
    isLive: Boolean(payload?.band?.isLive),
    viewerCount: Number(payload?.band?.viewerCount) || 0,
    requestsOpen: Boolean(payload?.band?.requestsOpen),
  },
  crate: {
    totalSongs: Number(payload?.crate?.totalSongs) || 0,
    artwork: Array.isArray(payload?.crate?.artwork) ? payload.crate.artwork : [],
  },
  nowPlaying: payload?.nowPlaying ?? null,
  // What the show is doing (ENG-700): playing, between (upNext waiting), idle or offline.
  stage: payload?.stage ?? (payload?.nowPlaying ? 'playing' : 'idle'),
  upNext: payload?.upNext ?? null,
  queue: Array.isArray(payload?.queue) ? payload.queue : [],
  highlights: payload?.highlights ? normalizeHighlights(payload.highlights) : EMPTY_HIGHLIGHTS,
});

/** A stored media path as a URL the browser can load. */
export const resolveMediaUrl = (url) => {
  if (!url) return null;
  const raw = String(url);
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = (config.mediaBaseUrl || '').replace(/\/$/, '');
  const path = raw.replace(/^\//, '');
  return base ? `${base}/${path}` : `/${path}`;
};
